import React, { useState, useEffect, useRef } from 'react';
import { getCardApplicationFee, initiateApplicationPayment } from '../../../api/cardService';
import { initiateGeneralPayment } from '../../../api/paymentService';
import { toast } from 'react-toastify';
import { FaCcVisa, FaCcMastercard, FaCreditCard, FaCheckCircle, FaTimesCircle, FaArrowLeft } from 'react-icons/fa';

const CardApplicationForm = () => {
    const [step, setStep] = useState(1); // 1: Application info, 2: Payment, 3: Gateway
    const [applicationFee, setApplicationFee] = useState(50.00);
    const [loading, setLoading] = useState(false);

    // Application form data
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        identityNumber: '',
        email: '',
        phone: '',
        userGender: ''
    });

    // Payment form data
    const [paymentData, setPaymentData] = useState({
        cardNumber: '',
        cardName: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: ''
    });
    const [paymentErrors, setPaymentErrors] = useState({});
    
    // Gateway state
    const [paymentHtml, setPaymentHtml] = useState(null);
    const [paymentOutcome, setPaymentOutcome] = useState(null); // 'success' | 'failed'
    const [resultMessage, setResultMessage] = useState('');
    const iframeRef = useRef(null);

    // Fetch application fee on mount
    useEffect(() => {
        const fetchFee = async () => {
            const res = await getCardApplicationFee();
            if (res.isSuccess && res.data?.fee) {
                setApplicationFee(res.data.fee);
            }
        };
        fetchFee();
    }, []);

    // Payment Result Listener
    useEffect(() => {
        const handleMessage = (event) => {
            const data = event.data;
            if (!data || data.type !== 'PAYMENT_RESULT') return;

            if (data.status === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kart başvurunuz başarıyla tamamlandı!');
                setPaymentHtml(null);
            } else if (data.status === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Iframe Load Handler (Fallback)
    const handleIframeLoad = () => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        try {
            const href = iframe.contentWindow?.location?.href;
            if (!href) return;
            
            const iframeUrl = new URL(href);
            const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
            const error = iframeUrl.searchParams.get('error');

            if (paymentStatus === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kart başvurunuz başarıyla tamamlandı!');
                setPaymentHtml(null);
            } else if (paymentStatus === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(error ? decodeURIComponent(error) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        } catch (err) {
            // CORS error expected when on bank page
        }
    };

    // Luhn Algorithm
    const luhnCheck = (val) => {
        let checksum = 0;
        let j = 1;
        for (let i = val.length - 1; i >= 0; i--) {
            let calc = 0;
            calc = Number(val.charAt(i)) * j;
            if (calc > 9) {
                checksum = checksum + 1;
                calc = calc - 10;
            }
            checksum = checksum + calc;
            if (j == 1) {
                j = 2;
            } else {
                j = 1;
            }
        }
        return (checksum % 10) == 0;
    };

    const detectCardType = (cardNumber) => {
        const cleaned = cardNumber.replace(/\s/g, '');
        if (cleaned.startsWith('4')) return '1'; // Visa
        if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2'; // MasterCard
        return null;
    };

    // Step 1: Validate and proceed to payment
    const handleContinueToPayment = () => {
        if (!formData.firstName || !formData.lastName || !formData.identityNumber || !formData.phone || !formData.userGender) {
            toast.error("Lütfen tüm zorunlu alanları doldurunuz.");
            return;
        }

        if (formData.identityNumber.length !== 11) {
            toast.error("TC Kimlik No 11 haneli olmalıdır.");
            return;
        }

        if (formData.phone.length !== 10 || !formData.phone.startsWith('5')) {
            toast.error("Geçerli bir telefon numarası giriniz (5XXXXXXXXX).");
            return;
        }

        setStep(2);
    };

    // Step 2: Process payment and submit application
    const handlePayment = async () => {
        setLoading(true);
        const errors = {};
        const pan = paymentData.cardNumber.replace(/\s/g, '');
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;

        // Validate Card Number
        if (!pan) {
            errors.cardNumber = 'Kart numarası gereklidir.';
        } else if (pan.length !== 16) {
            errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
        } else if (!luhnCheck(pan)) {
            errors.cardNumber = 'Geçersiz kart numarası.';
        }

        // Validate Card Name
        if (!paymentData.cardName.trim()) {
            errors.cardName = 'Kart üzerindeki isim gereklidir.';
        }

        // Validate Expiry
        if (!paymentData.expiryMonth || !paymentData.expiryYear) {
            errors.expiry = 'Son kullanma tarihi seçiniz.';
        } else {
            const expYear = parseInt(paymentData.expiryYear);
            const expMonth = parseInt(paymentData.expiryMonth);
            if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
                errors.expiry = 'Kartın son kullanma tarihi geçmiştir.';
            }
        }

        // Validate CVV
        if (!paymentData.cvv) {
            errors.cvv = 'CVV gereklidir.';
        } else if (paymentData.cvv.length !== 3) {
            errors.cvv = 'CVV 3 haneli olmalıdır.';
        }

        if (Object.keys(errors).length > 0) {
            setPaymentErrors(errors);
            setLoading(false);
            return;
        }

        setPaymentErrors({});

        try {
            // First, initiate application on backend (creates pending application + transaction)
            const initResult = await initiateApplicationPayment({
                firstName: formData.firstName,
                lastName: formData.lastName,
                identityNumber: formData.identityNumber,
                email: formData.email || "",
                phone: formData.phone,
                userGender: formData.userGender,
                pan: paymentData.cardNumber.replace(/\s/g, ''),
                cv2: paymentData.cvv,
                expireMonth: paymentData.expiryMonth.padStart(2, '0'),
                expireYear: paymentData.expiryYear,
                cardType: detectCardType(paymentData.cardNumber)
            });

            if (!initResult.isSuccess) {
                toast.error(initResult.resultMessage || "Başvuru oluşturulamadı.");
                setLoading(false);
                return;
            }

            // Now initiate 3D Secure payment using general payment endpoint (no FacilityID required)
            const paymentPayload = {
                amount: initResult.data.amount,
                paymentGroup: "CardApplicationFee",
                referenceId: String(initResult.data.transactionId),
                applicationId: initResult.data.applicationId, // For status update on success
                description: initResult.data.description || `Kart Başvuru Ücreti - ${formData.firstName} ${formData.lastName}`,
                
                // Card details
                pan: paymentData.cardNumber.replace(/\s/g, ''),
                cv2: paymentData.cvv,
                expireMonth: paymentData.expiryMonth.padStart(2, '0'),
                expireYear: paymentData.expiryYear,
                cardType: detectCardType(paymentData.cardNumber),
                
                // Buyer info
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email || ""
            };

            const paymentResult = await initiateGeneralPayment(paymentPayload);

            if (paymentResult.isSuccess && paymentResult.data?.html) {
                // Show inline 3D Secure gateway
                setPaymentHtml(paymentResult.data.html);
                setStep(3); // Gateway step
            } else {
                toast.error(paymentResult.resultMessage || "Ödeme başlatılamadı.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Reset form completely
    const resetForm = () => {
        setStep(1);
        setFormData({
            firstName: '',
            lastName: '',
            identityNumber: '',
            email: '',
            phone: '',
            userGender: ''
        });
        setPaymentData({
            cardNumber: '',
            cardName: '',
            expiryMonth: '',
            expiryYear: '',
            cvv: ''
        });
        setPaymentHtml(null);
        setPaymentOutcome(null);
        setResultMessage('');
    };

    return (
        <div className="card-application-section" style={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            padding: '40px 30px'
        }}>
            <h3 style={{ 
                marginBottom: '24px', 
                color: '#4a5568', 
                textAlign: 'center', 
                fontSize: '1.5rem', 
                fontWeight: '600' 
            }}>
                Kart Başvuru Formu
            </h3>

            {step === 1 ? (
                // Step 1: Application Information
                <>
                    <div style={{ 
                        background: '#fef3c7', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        marginBottom: '24px',
                        border: '1px solid #fbbf24'
                    }}>
                        <p style={{ margin: 0, fontSize: '0.95rem', color: '#92400e' }}>
                            <strong>Başvuru Ücreti:</strong> {applicationFee.toFixed(2)} TL
                        </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                Ad *
                            </label>
                            <input 
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                value={formData.firstName} 
                                onChange={e => setFormData({...formData, firstName: e.target.value})} 
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                Soyad *
                            </label>
                            <input 
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                value={formData.lastName} 
                                onChange={e => setFormData({...formData, lastName: e.target.value})} 
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                TC Kimlik No *
                            </label>
                            <input 
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                maxLength="11" 
                                value={formData.identityNumber} 
                                onChange={e => setFormData({...formData, identityNumber: e.target.value.replace(/\D/g, '').slice(0, 11)})} 
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                Telefon *
                            </label>
                            <input 
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                value={formData.phone} 
                                onChange={e => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})} 
                                placeholder="5XXXXXXXXX"
                                maxLength="10"
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                E-Posta
                            </label>
                            <input 
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                value={formData.email} 
                                onChange={e => setFormData({...formData, email: e.target.value})} 
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                Cinsiyet *
                            </label>
                            <select 
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                value={formData.userGender} 
                                onChange={e => setFormData({...formData, userGender: e.target.value})}
                            >
                                <option value="">Seçiniz</option>
                                <option value="Male">Erkek</option>
                                <option value="Female">Kadın</option>
                            </select>
                        </div>
                    </div>
                    
                    <button 
                        onClick={handleContinueToPayment} 
                        style={{ 
                            marginTop: '24px', 
                            width: '100%', 
                            padding: '14px', 
                            background: '#3b82f6', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            fontSize: '1rem'
                        }}
                    >
                        Ödemeye Geç
                    </button>
                </>
            ) : step === 2 ? (
                // Step 2: Payment
                <>
                    <div style={{ 
                        background: '#f0f9ff', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        marginBottom: '24px',
                        border: '1px solid #bae6fd'
                    }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#0369a1' }}>
                            <strong>Başvuran:</strong> {formData.firstName} {formData.lastName}<br />
                            <strong>TC:</strong> {formData.identityNumber}<br />
                            <strong>Ödenecek Tutar:</strong> {applicationFee.toFixed(2)} TL
                        </p>
                    </div>

                    <h4 style={{ 
                        marginBottom: '16px', 
                        color: '#4a5568', 
                        fontSize: '1.1rem', 
                        fontWeight: '600' 
                    }}>
                        Ödeme Bilgileri
                    </h4>

                    {/* Card Number */}
                    <div className="sa-form-group" style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                            Kart Numarası
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                value={paymentData.cardNumber}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                                    if (value.length <= 16) {
                                        setPaymentData({ ...paymentData, cardNumber: formatted });
                                        if (paymentErrors.cardNumber) setPaymentErrors({ ...paymentErrors, cardNumber: '' });
                                    }
                                }}
                                placeholder="0000 0000 0000 0000"
                                style={{
                                    width: '100%',
                                    padding: '12px 50px 12px 12px',
                                    borderRadius: '8px',
                                    border: paymentErrors.cardNumber ? '1px solid #ef4444' : '1px solid #e2e8f0',
                                    fontSize: '1rem'
                                }}
                                maxLength="19"
                            />
                            <div style={{ 
                                position: 'absolute', 
                                right: '12px', 
                                top: '50%', 
                                transform: 'translateY(-50%)' 
                            }}>
                                {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={28} color="#1a1f71" />}
                                {detectCardType(paymentData.cardNumber) === '2' && (
                                    <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                                        <circle cx="10" cy="10" r="10" fill="#EB001B" />
                                        <circle cx="22" cy="10" r="10" fill="#F79E1B" />
                                        <path d="M16 2 A10 10 0 0 0 16 18 A10 10 0 0 0 16 2 Z" fill="#FF5F00" />
                                    </svg>
                                )}
                                {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={28} color="#cbd5e1" />}
                            </div>
                        </div>
                        {paymentErrors.cardNumber && (
                            <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '4px' }}>
                                {paymentErrors.cardNumber}
                            </div>
                        )}
                    </div>

                    {/* Card Name */}
                    <div className="sa-form-group" style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                            Kart Üzerindeki İsim
                        </label>
                        <input
                            type="text"
                            value={paymentData.cardName}
                            onChange={(e) => {
                                setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() });
                                if (paymentErrors.cardName) setPaymentErrors({ ...paymentErrors, cardName: '' });
                            }}
                            placeholder="AD SOYAD"
                            style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '8px',
                                border: paymentErrors.cardName ? '1px solid #ef4444' : '1px solid #e2e8f0',
                                fontSize: '1rem'
                            }}
                        />
                        {paymentErrors.cardName && (
                            <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '4px' }}>
                                {paymentErrors.cardName}
                            </div>
                        )}
                    </div>

                    {/* Expiry & CVV */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <div className="sa-form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                Son Kullanma Tarihi
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select
                                    value={paymentData.expiryMonth}
                                    onChange={(e) => {
                                        setPaymentData({ ...paymentData, expiryMonth: e.target.value });
                                        if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: paymentErrors.expiry ? '1px solid #ef4444' : '1px solid #e2e8f0',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="">Ay</option>
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const month = String(i + 1).padStart(2, '0');
                                        return <option key={month} value={month}>{month}</option>;
                                    })}
                                </select>
                                <select
                                    value={paymentData.expiryYear}
                                    onChange={(e) => {
                                        setPaymentData({ ...paymentData, expiryYear: e.target.value });
                                        if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: paymentErrors.expiry ? '1px solid #ef4444' : '1px solid #e2e8f0',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="">Yıl</option>
                                    {Array.from({ length: 10 }, (_, i) => {
                                        const year = new Date().getFullYear() + i;
                                        return <option key={year} value={year}>{year}</option>;
                                    })}
                                </select>
                            </div>
                            {paymentErrors.expiry && (
                                <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '4px' }}>
                                    {paymentErrors.expiry}
                                </div>
                            )}
                        </div>

                        <div className="sa-form-group">
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                                CVV
                            </label>
                            <input
                                type="password"
                                value={paymentData.cvv}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                    setPaymentData({ ...paymentData, cvv: value });
                                    if (paymentErrors.cvv) setPaymentErrors({ ...paymentErrors, cvv: '' });
                                }}
                                placeholder="123"
                                maxLength="3"
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: paymentErrors.cvv ? '1px solid #ef4444' : '1px solid #e2e8f0',
                                    fontSize: '1rem'
                                }}
                            />
                            {paymentErrors.cvv && (
                                <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '4px' }}>
                                    {paymentErrors.cvv}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button 
                            onClick={() => {
                                setStep(1);
                                setPaymentData({
                                    cardNumber: '',
                                    cardName: '',
                                    expiryMonth: '',
                                    expiryYear: '',
                                    cvv: ''
                                });
                                setPaymentErrors({});
                            }}
                            disabled={loading}
                            style={{ 
                                flex: 1,
                                padding: '14px', 
                                background: '#e2e8f0', 
                                color: '#475569', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontWeight: 'bold', 
                                cursor: loading ? 'not-allowed' : 'pointer',
                                fontSize: '1rem'
                            }}
                        >
                            Geri
                        </button>
                        <button 
                            onClick={handlePayment} 
                            disabled={loading}
                            style={{ 
                                flex: 2,
                                padding: '14px', 
                                background: loading ? '#94a3b8' : '#e02d1b', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontWeight: 'bold', 
                                cursor: loading ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {loading ? 'İşleniyor...' : 'Ödemeyi Tamamla'}
                        </button>
                    </div>
                </>
            ) : (
                // Step 3: Gateway or Result
                <>
                    {paymentOutcome ? (
                        // Payment Result
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            background: paymentOutcome === 'success' ? '#f0fdf4' : '#fef2f2',
                            borderRadius: '12px',
                            border: `1px solid ${paymentOutcome === 'success' ? '#86efac' : '#fecaca'}`
                        }}>
                            {paymentOutcome === 'success' ? (
                                <FaCheckCircle style={{ fontSize: '64px', color: '#22c55e', marginBottom: '16px' }} />
                            ) : (
                                <FaTimesCircle style={{ fontSize: '64px', color: '#ef4444', marginBottom: '16px' }} />
                            )}
                            <h4 style={{ 
                                color: paymentOutcome === 'success' ? '#16a34a' : '#dc2626',
                                fontSize: '1.4rem',
                                marginBottom: '12px'
                            }}>
                                {paymentOutcome === 'success' ? 'Başvurunuz Tamamlandı!' : 'Ödeme Başarısız'}
                            </h4>
                            <p style={{ color: '#6b7280', marginBottom: '24px' }}>{resultMessage}</p>
                            
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                {paymentOutcome === 'failed' && (
                                    <button
                                        onClick={() => { setPaymentOutcome(null); setStep(2); }}
                                        style={{
                                            padding: '12px 24px',
                                            background: '#3b82f6',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Tekrar Dene
                                    </button>
                                )}
                                <button
                                    onClick={resetForm}
                                    style={{
                                        padding: '12px 24px',
                                        background: paymentOutcome === 'success' ? '#22c55e' : '#ef4444',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {paymentOutcome === 'success' ? 'Yeni Başvuru' : 'İptal Et'}
                                </button>
                            </div>
                        </div>
                    ) : paymentHtml ? (
                        // 3D Secure Iframe
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                padding: '16px',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#f8fafc',
                                borderRadius: '8px 8px 0 0'
                            }}>
                                <h4 style={{ margin: 0, fontSize: '16px', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaArrowLeft 
                                        onClick={() => { setPaymentHtml(null); setStep(2); }}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    Güvenli Ödeme Ekranı
                                </h4>
                                <span style={{ fontSize: '14px', color: '#64748b' }}>
                                    {applicationFee.toFixed(2)} TL
                                </span>
                            </div>
                            <iframe
                                ref={iframeRef}
                                title="3D Secure Payment"
                                srcDoc={paymentHtml}
                                onLoad={handleIframeLoad}
                                style={{ 
                                    width: '100%', 
                                    height: '550px', 
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0 0 8px 8px'
                                }}
                            />
                        </div>
                    ) : (
                        // Loading fallback
                        <div style={{ textAlign: 'center', padding: '60px' }}>
                            <p>Ödeme sayfası yükleniyor...</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CardApplicationForm;
